import { useState, useCallback, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';

export type StepStatus = 'idle' | 'active' | 'done' | 'error';
export type Level = 'info' | 'warn' | 'error';

export interface ImportStep {
  key: string;
  label: string;
  status: StepStatus;
  count?: number;
}

export interface LogEntry {
  id: string;
  ts: number;
  level: Level;
  row?: number | null;
  message: string;
  meta?: Record<string, any>;
}

/** Annotated Excel αποτελεσμάτων για λήψη μετά την εισαγωγή */
export type ImportResultFile = {
  blob: Blob;
  fileName: string;
};

export interface ImportState {
  running: boolean;
  progress: number; // 0..100
  steps: ImportStep[]; // 8 fixed steps
  processed: number;
  inserted: number;
  duplicates: number;
  errors: number;
  worksheetName?: string | null;
  logs: LogEntry[];
  outcome?: { status: 'success' | 'partial' | 'empty' | 'aborted'; reasons: string[] };
  terminatedByEmptyRows?: { atRow: number } | null;
  /** Αρχείο Excel με στήλες αποτελέσματος ανά γραμμή */
  resultFile?: ImportResultFile | null;
}

// Fixed steps with default Greek labels
const DEFAULT_STEPS: ImportStep[] = [
  { key: 'load', label: 'Φόρτωση αρχείου', status: 'idle', count: 0 },
  { key: 'parse', label: 'Ανάγνωση βιβλίου εργασίας', status: 'idle', count: 0 },
  { key: 'header', label: 'Εντοπισμός επικεφαλίδας', status: 'idle', count: 0 },
  { key: 'map', label: 'Αντιστοίχιση στηλών', status: 'idle', count: 0 },
  { key: 'iterate', label: 'Επεξεργασία γραμμών', status: 'idle', count: 0 },
  { key: 'validate', label: 'Έλεγχος αναφορών', status: 'idle', count: 0 },
  { key: 'check', label: 'Έλεγχος διπλοτύπων', status: 'idle', count: 0 },
  { key: 'insert', label: 'Καταχώριση', status: 'idle', count: 0 },
];

export interface ImportProgressAPI {
  start: (worksheetName?: string) => void;
  stop: () => void;
  setProgress: (progress: number) => void;
  setStep: (key: string, status: StepStatus) => void;
  advanceTo: (key: string) => void;
  incProcessed: () => void;
  incInserted: () => void;
  incDuplicate: () => void;
  incError: () => void;
  log: (level: Level, message: string, row?: number, meta?: any) => void;
  reset: () => void;
  setWorksheetName: (name?: string | null) => void;
  getState: () => ImportState;
  incStepCount: (key: string) => void;
  setTerminatedByEmptyRows: (atRow: number) => void;
  setOutcome: (outcome: ImportState['outcome']) => void;
  setResultFile: (file: ImportResultFile | null) => void;
}

const createInitialState = (): ImportState => ({
  running: false,
  progress: 0,
  steps: [...DEFAULT_STEPS],
  processed: 0,
  inserted: 0,
  duplicates: 0,
  errors: 0,
  worksheetName: null,
  logs: [],
  outcome: undefined,
  terminatedByEmptyRows: null,
  resultFile: null,
});

export function useImportProgress(appStore: any): [ImportState, ImportProgressAPI] {
  const [state, setState] = useState<ImportState>(createInitialState);
  const logCountRef = useRef(0);
  const stateRef = useRef<ImportState>(state);

  // Keep stateRef in sync with state for getState()
  const updateState = useCallback((updater: (prev: ImportState) => ImportState) => {
    setState((prev) => {
      const newState = updater(prev);
      stateRef.current = newState;
      return newState;
    });
  }, []);

  const start = useCallback(
    (worksheetName?: string) => {
      updateState((prev) => ({
        ...prev,
        running: true,
        progress: 0,
        processed: 0,
        inserted: 0,
        duplicates: 0,
        errors: 0,
        worksheetName: worksheetName || null,
        logs: [],
        outcome: undefined,
        terminatedByEmptyRows: null,
        resultFile: null,
        steps: prev.steps.map((step) => ({ ...step, status: 'idle' as StepStatus, count: 0 })),
      }));
      logCountRef.current = 0;
    },
    [updateState],
  );

  const stop = useCallback(() => {
    updateState((prev) => ({
      ...prev,
      running: false,
    }));
  }, [updateState]);

  const setProgress = useCallback(
    (progress: number) => {
      updateState((prev) => ({
        ...prev,
        progress: Math.max(0, Math.min(100, progress)),
      }));
    },
    [updateState],
  );

  const setStep = useCallback(
    (key: string, status: StepStatus) => {
      updateState((prev) => ({
        ...prev,
        steps: prev.steps.map((step) => (step.key === key ? { ...step, status } : step)),
      }));
    },
    [updateState],
  );

  const advanceTo = useCallback(
    (key: string) => {
      updateState((prev) => {
        const currentActiveIndex = prev.steps.findIndex((step) => step.status === 'active');
        const targetIndex = prev.steps.findIndex((step) => step.key === key);

        return {
          ...prev,
          steps: prev.steps.map((step, index) => {
            if (index === currentActiveIndex && currentActiveIndex !== -1) {
              return { ...step, status: 'done' as StepStatus };
            }
            if (index === targetIndex) {
              return { ...step, status: 'active' as StepStatus };
            }
            return step;
          }),
        };
      });
    },
    [updateState],
  );

  const incProcessed = useCallback(() => {
    updateState((prev) => ({
      ...prev,
      processed: prev.processed + 1,
    }));
  }, [updateState]);

  const incInserted = useCallback(() => {
    updateState((prev) => ({
      ...prev,
      inserted: prev.inserted + 1,
    }));
  }, [updateState]);

  const incDuplicate = useCallback(() => {
    updateState((prev) => ({
      ...prev,
      duplicates: prev.duplicates + 1,
    }));
  }, [updateState]);

  const incError = useCallback(() => {
    updateState((prev) => ({
      ...prev,
      errors: prev.errors + 1,
    }));
  }, [updateState]);

  const log = useCallback(
    (level: Level, message: string, row?: number, meta?: any) => {
      const entry: LogEntry = {
        id: uuidv4(),
        ts: Date.now(),
        level,
        row: row ?? null,
        message,
        meta,
      };

      updateState((prev) => ({
        ...prev,
        logs: [...prev.logs, entry],
      }));

      logCountRef.current += 1;
    },
    [updateState],
  );

  const reset = useCallback(() => {
    const initialState = createInitialState();
    setState(initialState);
    stateRef.current = initialState;
    logCountRef.current = 0;
  }, []);

  const setWorksheetName = useCallback(
    (name?: string | null) => {
      updateState((prev) => ({
        ...prev,
        worksheetName: name || null,
      }));
    },
    [updateState],
  );

  const getState = useCallback((): ImportState => {
    return stateRef.current;
  }, []);

  const incStepCount = useCallback(
    (key: string) => {
      updateState((prev) => ({
        ...prev,
        steps: prev.steps.map((step) =>
          step.key === key ? { ...step, count: (step.count || 0) + 1 } : step,
        ),
      }));
    },
    [updateState],
  );

  const setTerminatedByEmptyRows = useCallback(
    (atRow: number) => {
      updateState((prev) => ({
        ...prev,
        terminatedByEmptyRows: { atRow },
      }));
    },
    [updateState],
  );

  const setOutcome = useCallback(
    (outcome: ImportState['outcome']) => {
      updateState((prev) => ({
        ...prev,
        outcome,
      }));
    },
    [updateState],
  );

  const setResultFile = useCallback(
    (file: ImportResultFile | null) => {
      updateState((prev) => ({
        ...prev,
        resultFile: file,
      }));
    },
    [updateState],
  );

  const api: ImportProgressAPI = {
    start,
    stop,
    setProgress,
    setStep,
    advanceTo,
    incProcessed,
    incInserted,
    incDuplicate,
    incError,
    log,
    reset,
    setWorksheetName,
    getState,
    incStepCount,
    setTerminatedByEmptyRows,
    setOutcome,
    setResultFile,
  };

  return [state, api];
}
